/**
 * Inline Payment Component
 * Renders Stripe Payment Element directly into a container element
 */

interface InlinePaymentOptions {
  container: string | HTMLElement;
  clientSecret: string;
  publishableKey: string;
  stripeAccountId?: string;
  amount?: number;
  currency?: string;
  onSuccess: (result: any) => void;
  onError: (error: Error) => void;
  onReady?: () => void;
}

const INLINE_STYLES = `
  .khaime-inline-payment {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .khaime-inline-payment * {
    box-sizing: border-box;
  }

  .khaime-inline-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: #6b7280;
  }

  .khaime-inline-spinner {
    width: 24px;
    height: 24px;
    border: 3px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: khaime-spin 0.8s linear infinite;
    margin-right: 0.75rem;
  }

  @keyframes khaime-spin {
    to { transform: rotate(360deg); }
  }

  .khaime-inline-error {
    padding: 0.75rem 1rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 0.375rem;
    color: #dc2626;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .khaime-stripe-container {
    padding: 1rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
  }

  .khaime-pay-button {
    width: 100%;
    padding: 0.875rem 1.5rem;
    background: #000;
    color: #fff;
    border: none;
    border-radius: 0.375rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.1s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .khaime-pay-button:hover:not(:disabled) {
    background: #1f2937;
  }

  .khaime-pay-button:active:not(:disabled) {
    transform: scale(0.98);
  }

  .khaime-pay-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .khaime-pay-button svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }

  .khaime-pay-button .spinner {
    width: 18px;
    height: 18px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: khaime-spin 0.8s linear infinite;
  }

  .khaime-secure-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    margin-top: 0.75rem;
    font-size: 0.75rem;
    color: #9ca3af;
  }

  .khaime-secure-badge svg {
    width: 12px;
    height: 12px;
  }

  .khaime-success {
    text-align: center;
    padding: 2rem;
  }

  .khaime-success-icon {
    width: 48px;
    height: 48px;
    background: #dcfce7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
  }

  .khaime-success-icon svg {
    width: 24px;
    height: 24px;
    color: #16a34a;
  }

  .khaime-success h3 {
    margin: 0 0 0.5rem;
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
  }

  .khaime-success p {
    margin: 0;
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

/**
 * Load Stripe.js dynamically
 */
async function loadStripe(publishableKey: string, stripeAccountId?: string): Promise<any> {
  const stripeOptions = stripeAccountId ? { stripeAccount: stripeAccountId } : undefined;

  if ((window as any).Stripe) {
    return (window as any).Stripe(publishableKey, stripeOptions);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve((window as any).Stripe(publishableKey, stripeOptions));
    script.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(script);
  });
}

/**
 * Format currency amount for display
 */
function formatAmount(amount: number, currency: string): string {
  const curr = currency.toUpperCase();
  const symbols: Record<string, string> = {
    USD: '$', EUR: '€', GBP: '£', NGN: '₦', GHS: 'GH₵',
    KES: 'KSh', ZAR: 'R', CAD: 'C$', AUD: 'A$',
  };
  const symbol = symbols[curr] || curr + ' ';
  return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Render payment form inline into a container
 */
export async function renderInlinePayment(
  options: InlinePaymentOptions
): Promise<{ error?: { message: string }; paymentIntent?: any }> {
  const {
    container,
    clientSecret,
    publishableKey,
    stripeAccountId,
    amount,
    currency = 'USD',
    onSuccess,
    onError,
    onReady,
  } = options;

  // Get container element
  const containerEl = typeof container === 'string'
    ? document.getElementById(container)
    : container;

  if (!containerEl) {
    const err = new Error(`Container element not found: ${container}`);
    onError(err);
    return { error: { message: err.message } };
  }

  // Inject styles
  if (!document.getElementById('khaime-inline-styles')) {
    const styleEl = document.createElement('style');
    styleEl.id = 'khaime-inline-styles';
    styleEl.textContent = INLINE_STYLES;
    document.head.appendChild(styleEl);
  }

  // Show loading state
  containerEl.innerHTML = `
    <div class="khaime-inline-payment">
      <div class="khaime-inline-loading">
        <div class="khaime-inline-spinner"></div>
        <span>Loading payment form...</span>
      </div>
    </div>
  `;

  try {
    // Load Stripe
    const stripe = await loadStripe(publishableKey, stripeAccountId);

    if (!stripe) {
      throw new Error('Failed to initialize payment processor');
    }

    // Create elements
    const elements = stripe.elements({ clientSecret });
    const paymentElement = elements.create('payment');

    // Build the payment form HTML
    const amountDisplay = amount ? formatAmount(amount, currency) : '';
    const lockIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;

    containerEl.innerHTML = `
      <div class="khaime-inline-payment">
        <div id="khaime-stripe-element" class="khaime-stripe-container"></div>
        <div id="khaime-error" class="khaime-inline-error" style="display: none;"></div>
        <button id="khaime-pay-btn" class="khaime-pay-button" disabled>
          ${lockIcon}
          ${amountDisplay ? `Pay ${amountDisplay}` : 'Pay Now'}
        </button>
        <div class="khaime-secure-badge">
          ${lockIcon}
          <span>Secured by Khaime</span>
        </div>
      </div>
    `;

    // Mount the payment element
    paymentElement.mount('#khaime-stripe-element');

    const payBtn = containerEl.querySelector('#khaime-pay-btn') as HTMLButtonElement;
    const errorDiv = containerEl.querySelector('#khaime-error') as HTMLDivElement;

    // Enable button when ready
    paymentElement.on('ready', () => {
      payBtn.disabled = false;
      onReady?.();
    });

    // Handle validation errors
    paymentElement.on('change', (event: any) => {
      if (event.error) {
        errorDiv.textContent = event.error.message;
        errorDiv.style.display = 'block';
      } else {
        errorDiv.style.display = 'none';
      }
    });

    // Return a promise that resolves when payment completes
    return new Promise((resolve) => {
      payBtn.addEventListener('click', async () => {
        payBtn.disabled = true;
        payBtn.innerHTML = '<span class="spinner"></span> Processing...';
        errorDiv.style.display = 'none';

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

        if (error) {
          errorDiv.textContent = error.message || 'Payment failed. Please try again.';
          errorDiv.style.display = 'block';
          payBtn.disabled = false;
          payBtn.innerHTML = `${lockIcon} ${amountDisplay ? `Pay ${amountDisplay}` : 'Pay Now'}`;
          onError(new Error(error.message || 'Payment failed'));
          resolve({ error: { message: error.message || 'Payment failed' } });
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          // Show success state
          containerEl.innerHTML = `
            <div class="khaime-inline-payment">
              <div class="khaime-success">
                <div class="khaime-success-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
                <h3>Payment Successful</h3>
                <p>Your payment has been processed.</p>
              </div>
            </div>
          `;
          onSuccess(paymentIntent);
          resolve({ paymentIntent });
        } else {
          // Handle other statuses (requires_action, etc.)
          errorDiv.textContent = 'Payment requires additional action. Please try again.';
          errorDiv.style.display = 'block';
          payBtn.disabled = false;
          payBtn.innerHTML = `${lockIcon} ${amountDisplay ? `Pay ${amountDisplay}` : 'Pay Now'}`;
        }
      });
    });

  } catch (err) {
    const error = err instanceof Error ? err : new Error('Payment failed');
    containerEl.innerHTML = `
      <div class="khaime-inline-payment">
        <div class="khaime-inline-error">${error.message}</div>
      </div>
    `;
    onError(error);
    return { error: { message: error.message } };
  }
}

export default renderInlinePayment;
